//#region src/dp-lifecycle-status.ts
/**
* ⛔ EXHAUSTIVE BY CONSTRUCTION, and it is now the **runtime source of the
* vocabulary too** — {@link DP_LIFECYCLE_STATUSES} is its keys.
*
* `Record<DpLifecycleStatus, …>` means adding a member to the contracts union
* without classifying it here **fails to compile**, which is the whole point:
* a new platform state cannot be silently omitted from the filter, the
* checkbox group, or the two consumers that read the derived set.
* ⛔ Do not replace this with a lookup carrying a default arm, and do not
* re-introduce a separately-maintained array of the members.
*
* ⚠️ Key ORDER is lifecycle order, and it is load-bearing: it is what the
* checkbox group renders, and an operator reads
* `provisioning → ready → active` as a progression. Sorting alphabetically
* would scatter it.
*/
const DP_SERVICE_CLASS = {
	provisioning: "in-service",
	ready: "in-service",
	active: "in-service",
	degraded: "in-service",
	failed: "in-service",
	draining: "in-service",
	disabling: "in-service",
	disabled: "in-service",
	restoring: "in-service",
	destroying: "out-of-service",
	destroyed: "out-of-service",
	decommissioned: "out-of-service"
};
/**
* Every declared lifecycle status, in lifecycle order.
*
* ⚠️ Derived from {@link DP_SERVICE_CLASS}'s keys rather than written out
* again — a second hand-maintained list of the same members is exactly the rot
* this module exists to delete. The single assertion is unavoidable
* (`Object.keys` is typed `string[]`) and is sound because the object's type
* fixes its keys; it is NOT a double `as unknown as` cast.
*/
const DP_LIFECYCLE_STATUSES = Object.keys(DP_SERVICE_CLASS);
/**
* ⭐ **THE CLASSES, AS SETS — one `filter` per class over the one classification.**
*
* ⛔ `Record<DpServiceClass, …>` so a third class cannot be added to
* {@link DpServiceClass} without giving it a set here. ⛔ And both entries read
* {@link DP_SERVICE_CLASS}, so the two sets cannot be edited into disagreement:
* there is no second hand-maintained array to fall out of step, which is what
* "single-sourced" has to mean if it is to mean anything.
*
* ⚠️ The two sets **PARTITION** the vocabulary — every member in exactly one,
* none in both, none in neither — and `dp-lifecycle.spec.ts` asserts it rather
* than trusting it. That is load-bearing now that `?status=in-service` is a
* request an operator can make: a state that fell outside both classes would
* make the alias return an INCOMPLETE table, which is a wrong answer rather than
* an error, and nothing else in the system would notice.
*/
const DP_STATUSES_BY_CLASS = {
	"in-service": DP_LIFECYCLE_STATUSES.filter((s) => DP_SERVICE_CLASS[s] === "in-service"),
	"out-of-service": DP_LIFECYCLE_STATUSES.filter((s) => DP_SERVICE_CLASS[s] === "out-of-service")
};
/**
* Statuses NOT part of the operating fleet. **Derived — never hand-listed.**
*
* ⛔ An unrecognised status (the column is free text) is deliberately NOT in
* this set, so an unknown state is never filed among the corpses. Failing open
* is the safe direction: a spurious row is cosmetic, a silently hidden live data
* plane is operational.
*
* ⚠️ TWO CALLER KINDS, AND THE FAIL-OPEN DIRECTION MEANS DIFFERENT THINGS TO THEM.
* For a READ path (the placement picker, the DP table) fail-open errs toward SHOWING a row, which
* is the safe direction and the reason this behaviour exists.
* ⛔ For a WRITE door (`assertAppMayUseDataPlane`, compute-classes D48(a)) fail-open errs toward
* ADMITTING the write on an unrecognised status. That is accepted DELIBERATELY and is not an
* oversight: `platform.data_placements.status` is free text, the vocabulary is
* {@link DP_LIFECYCLE_STATUSES}-complete by construction (a new platform state cannot compile
* without being classified), and refusing every unknown value would make an unrecognised status a
* fleet-wide write outage. ⇒ The guard's job is to refuse the KNOWN-terminal states, not to be the
* last line of defence — the resource-permission check is. If that ever stops being true, this
* predicate is the wrong place to fix it: add an explicit allow-list arm at the write door.
*/
const OUT_OF_SERVICE_DP_STATUSES = DP_STATUSES_BY_CLASS["out-of-service"];
/**
* The operating fleet, and what `?status=in-service` resolves to. Derived from
* the same table as its complement, so the console's default view and this
* server-side class cannot drift apart.
*/
const IN_SERVICE_DP_STATUSES = DP_STATUSES_BY_CLASS["in-service"];
/**
* ⭐ **Is this data plane out of the operating fleet?** — the one server-side
* derivation of that boolean, for a **free-text** status read straight out of
* `platform.data_placements.status`.
*
* ⛔ **Nothing may re-derive this client-side.** The console's old placement
* picker did (`site-admin/platform/src/tenants/logic.ts:32`,
* `UNPLACEABLE_STATUSES`) and got `destroying` wrong — the same bug one state
* over — while the server enforced nothing at all. Every `outOfService` flag on
* the wire is this function's answer, so a client renders a decision rather than
* making one.
*
* ⚠️ Fails **open** on an unrecognised value, matching
* {@link OUT_OF_SERVICE_DP_STATUSES}: an unknown status is not filed among the
* corpses, because hiding a live data plane is operational where showing a
* spurious one is cosmetic.
*/
function isOutOfServiceDpStatus(value) {
	return OUT_OF_SERVICE_DP_STATUSES.includes(value);
}
//#endregion
export { DP_LIFECYCLE_STATUSES, DP_SERVICE_CLASS, DP_STATUSES_BY_CLASS, IN_SERVICE_DP_STATUSES, OUT_OF_SERVICE_DP_STATUSES, isOutOfServiceDpStatus };

//# sourceMappingURL=dp-lifecycle-status.js.map